/**
 * Acteurs clés de l’écosystème tech / innovation du bassin de Besançon.
 * Section « Cartographie » de la page d’accueil (src/home/sections/ActeursCles.js).
 *
 * Règles : ni classement ni annuaire exhaustif ; aucune donnée chiffrée ;
 * uniquement des descriptions qualitatives et des liens vers les pages d’accueil officielles.
 * Les URL sont les adresses finales (HTTP 200, sans redirection) : les pages d’accueil
 * transmises par le client (femto-st.fr/, univ-fcomte.fr/, micronora.com avec www, cnrs.fr/)
 * redirigent toutes vers celles-ci, et ensmm.fr ne répond plus. Toute modification doit
 * repasser par la vérification des liens.
 *
 * Dénominations : on emploie la graphie officielle actuelle des établissements
 * (« Université Marie et Louis Pasteur », héritière de l’Université de Franche-Comté ;
 * « SUPMICROTECH-ENSMM », graphie majoritaire dans les articles du blog ;
 * « GRETA-CFA Franche-Comté », nom du réseau). Le blog (lecture seule) contient encore
 * une variante « SupMicroTech-ENSMM » dans un article : à harmoniser côté blog.
 */
import { ReactComponent as CpuIcon } from "feather-icons/dist/icons/cpu.svg";
import { ReactComponent as BookOpenIcon } from "feather-icons/dist/icons/book-open.svg";
import { ReactComponent as ToolIcon } from "feather-icons/dist/icons/tool.svg";
import { ReactComponent as LayersIcon } from "feather-icons/dist/icons/layers.svg";
import { ReactComponent as CalendarIcon } from "feather-icons/dist/icons/calendar.svg";
import { ReactComponent as ActivityIcon } from "feather-icons/dist/icons/activity.svg";

/**
 * Fiche officielle de la formation « Développeur en intelligence artificielle » du GRETA-CFA Franche-Comté.
 * Adresse canonique (l’ancienne URL …/developpeur-intelligence-artificielle/ n’est plus qu’une redirection).
 * Utilisée uniquement par la section « Se former » (lien secondaire).
 */
export const GRETA_FORMATION_IA_URL =
  "https://greta-franche-comte.fr/formation/developpeur-en-intelligence-artificielle-lycee-polyvalent-jules-haag/";

/** Les six institutions structurantes affichées en cartes (`icon` : composant feather, décoratif). */
export const acteurs = [
  {
    name: "FEMTO-ST",
    kind: "Institut de recherche",
    icon: CpuIcon,
    text:
      "Institut de recherche en sciences pour l’ingénieur, rattaché notamment au CNRS et à l’université : micro- et nanotechnologies, automatique, informatique, énergie, optique.",
    url: "https://www.femto-st.fr/fr",
  },
  {
    name: "Université Marie et Louis Pasteur",
    kind: "Université",
    icon: BookOpenIcon,
    text:
      "L’université du territoire, héritière de l’Université de Franche-Comté : formations de la licence au doctorat, laboratoires et vie étudiante, avec un ancrage historique à Besançon.",
    url: "https://www.umlp.fr/",
  },
  {
    name: "SUPMICROTECH-ENSMM",
    kind: "École d’ingénieurs",
    icon: ToolIcon,
    text:
      "École nationale supérieure de mécanique et des microtechniques, aujourd’hui SUPMICROTECH : elle forme des ingénieurs en mécanique, microtechniques et systèmes, au cœur de la spécialité historique de la ville.",
    url: "https://www.supmicrotech.fr/",
  },
  {
    name: "TEMIS",
    kind: "Technopole",
    icon: LayersIcon,
    text:
      "Technopole microtechnique et scientifique de Besançon : entreprises, laboratoires et formations réunis sur un même site dédié à l’innovation.",
    url: "https://www.temis.org/",
  },
  {
    name: "Micronora",
    kind: "Salon professionnel",
    icon: CalendarIcon,
    text:
      "Salon international des microtechniques et de la précision organisé à Besançon, qui réunit industriels, laboratoires et fournisseurs de la filière.",
    url: "https://micronora.com/",
  },
  {
    name: "CHU de Besançon",
    kind: "Centre hospitalier universitaire",
    icon: ActivityIcon,
    text:
      "L’hôpital universitaire du territoire, où se rencontrent soins, recherche clinique et innovation en santé — un terrain d’application naturel pour l’IA.",
    url: "https://www.chu-besancon.fr/",
  },
];

/** Les autres acteurs autorisés, cités dans la ligne « Et aussi » (pages d’accueil uniquement). */
export const autres = [
  { name: "GRETA-CFA Franche-Comté", url: "https://greta-franche-comte.fr/" },
  { name: "Grand Besançon Métropole", url: "https://www.grandbesancon.fr/" },
  { name: "Ville de Besançon", url: "https://www.besancon.fr/" },
  { name: "CNRS", url: "https://www.cnrs.fr/fr" },
];
